import * as React from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, PlayCircle, X, RotateCw, ArrowUpCircle, ShieldOff, BadgeDollarSign } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { env } from '@/lib/env';
import { cn } from '@/lib/cn';

interface TenantRow {
  id: string;
  slug: string;
  legal_name: string;
  nip: string | null;
  vertical: string;
  tier: string;
  status: string;
  preview_version: number;
  live_version: number | null;
  custom_host: string | null;
  contact_email: string | null;
  created_at: number;
}

export const Route = createFileRoute('/_app/operator/tenants')({
  staticData: { crumb: 'Klienci' },
  beforeLoad: ({ context }) => {
    const ctx = context as { session: { role: string } };
    if (ctx.session.role !== 'operator') {
      throw redirect({ to: '/' });
    }
  },
  component: OperatorTenants,
});

type BulkKind = 'regenerate' | 'promote' | 'set_tier' | 'suspend';
type Tier = 'pozyskaj' | 'preview' | 'start' | 'pro' | 'pro_bundle' | 'multi';

interface BulkJob {
  id: string;
  kind: BulkKind;
  state: 'queued' | 'running' | 'cancelled' | 'completed' | 'failed';
  total: number;
  succeeded: number;
  failed: number;
  created_at: number;
  updated_at: number;
}

function OperatorTenants() {
  const [q, setQ] = React.useState('');
  const debouncedQ = useDebouncedValue(q, 250);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const qc = useQueryClient();

  const tenants = useQuery({
    queryKey: ['operator', 'tenants', debouncedQ],
    queryFn: async (): Promise<TenantRow[]> => {
      const url = new URL(`${env.apiBaseUrl}/api/tenants`, window.location.origin);
      if (debouncedQ) url.searchParams.set('q', debouncedQ);
      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) throw new Error('failed_to_load_tenants');
      return (await res.json()) as TenantRow[];
    },
  });

  const jobs = useQuery({
    queryKey: ['operator', 'bulk-jobs'],
    queryFn: async (): Promise<BulkJob[]> => {
      const res = await fetch(`${env.apiBaseUrl}/api/operator/bulk`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('failed_to_load_jobs');
      const j = (await res.json()) as { jobs: BulkJob[] };
      return j.jobs;
    },
    refetchInterval: 10_000,
  });

  const runBulk = useMutation({
    mutationFn: async (input: { kind: BulkKind; payload?: { tier?: Tier } }) => {
      const res = await fetch(`${env.apiBaseUrl}/api/operator/bulk`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          kind: input.kind,
          tenant_ids: [...selected],
          payload: input.payload,
        }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'bulk_failed');
      }
      return res.json();
    },
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['operator', 'bulk-jobs'] });
      qc.invalidateQueries({ queryKey: ['operator', 'tenants'] });
    },
  });

  const toggle = (id: string) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    const rows = tenants.data ?? [];
    if (selected.size === rows.length && rows.length > 0) setSelected(new Set());
    else setSelected(new Set(rows.map((t) => t.id)));
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">Klienci</h1>
        <p className="mt-1 text-sm text-text-muted">
          Wszystkie kliniki w systemie. Wybierz wiersz, aby otworzyć panel kliniki
          (impersonacja audytowana).
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-subtle"
            aria-hidden="true"
          />
          <Input
            placeholder="Szukaj po nazwie, slug, NIP, domenie…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {selected.size > 0 ? (
        <BulkToolbar
          count={selected.size}
          busy={runBulk.isPending}
          onCancel={() => setSelected(new Set())}
          onRun={(kind, payload) => runBulk.mutate({ kind, payload })}
          error={runBulk.error ? (runBulk.error as Error).message : null}
        />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{tenants.data?.length ?? 0} klinik</CardTitle>
          <CardDescription>
            Sortowane domyślnie po dacie utworzenia. Max 200 wyników w jednym żądaniu.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {tenants.isPending ? (
            <p className="p-6 text-sm text-text-muted">Ładowanie…</p>
          ) : tenants.isError ? (
            <p className="p-6 text-sm text-error">Nie udało się pobrać listy.</p>
          ) : tenants.data!.length === 0 ? (
            <p className="p-6 text-sm text-text-muted">Brak wyników.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[60rem] text-sm">
                <thead className="bg-surface text-left text-xs uppercase tracking-wide text-text-subtle">
                  <tr>
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        aria-label="Zaznacz wszystkich"
                        checked={
                          (tenants.data?.length ?? 0) > 0 &&
                          selected.size === tenants.data!.length
                        }
                        onChange={toggleAll}
                        className="size-4 rounded border-border text-accent focus:ring-accent"
                      />
                    </th>
                    <th className="px-4 py-3 font-medium">Klinika</th>
                    <th className="px-4 py-3 font-medium">Slug</th>
                    <th className="px-4 py-3 font-medium">Plan</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Live / Preview</th>
                    <th className="px-4 py-3 font-medium">Kontakt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-bg">
                  {tenants.data!.map((t) => (
                    <tr
                      key={t.id}
                      className={cn(
                        'hover:bg-surface/60',
                        selected.has(t.id) && 'bg-accent/5'
                      )}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          aria-label={`Zaznacz ${t.legal_name}`}
                          checked={selected.has(t.id)}
                          onChange={() => toggle(t.id)}
                          className="size-4 rounded border-border text-accent focus:ring-accent"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-text">{t.legal_name}</p>
                        {t.nip ? (
                          <p className="text-xs text-text-subtle font-mono">NIP {t.nip}</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-text-muted">{t.slug}</td>
                      <td className="px-4 py-3 text-text-muted">{t.tier}</td>
                      <td className="px-4 py-3">
                        <span
                          className={
                            t.status === 'live'
                              ? 'rounded-full bg-success/10 px-2 py-0.5 text-xs text-success ring-1 ring-success/20'
                              : t.status === 'paused'
                              ? 'rounded-full bg-warning/10 px-2 py-0.5 text-xs text-warning ring-1 ring-warning/20'
                              : 'rounded-full bg-surface px-2 py-0.5 text-xs text-text-muted ring-1 ring-border'
                          }
                        >
                          {t.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-text-muted tabular-nums">
                        {t.live_version ?? '—'} / {t.preview_version}
                      </td>
                      <td className="px-4 py-3 text-xs text-text-muted">
                        {t.contact_email ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <BulkJobsPanel jobs={jobs.data ?? []} loading={jobs.isPending} />
    </div>
  );
}

const KIND_LABEL: Record<BulkKind, string> = {
  regenerate: 'Regeneruj stronę',
  promote: 'Opublikuj podgląd jako live',
  set_tier: 'Ustaw plan',
  suspend: 'Zawieś',
};

const KIND_ICON: Record<BulkKind, React.ComponentType<{ className?: string }>> = {
  regenerate: RotateCw,
  promote: ArrowUpCircle,
  set_tier: BadgeDollarSign,
  suspend: ShieldOff,
};

function BulkToolbar({
  count,
  busy,
  onRun,
  onCancel,
  error,
}: {
  count: number;
  busy: boolean;
  onRun: (kind: BulkKind, payload?: { tier?: Tier }) => void;
  onCancel: () => void;
  error: string | null;
}) {
  const [kind, setKind] = React.useState<BulkKind>('regenerate');
  const [tier, setTier] = React.useState<Tier>('pozyskaj');
  const [confirm, setConfirm] = React.useState(false);

  const onClick = () => {
    if (!confirm) {
      setConfirm(true);
      return;
    }
    onRun(kind, kind === 'set_tier' ? { tier } : undefined);
    setConfirm(false);
  };

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-accent/10 px-3 py-1 text-xs font-semibold text-accent ring-1 ring-accent/20">
            Zaznaczono {count}
          </span>
          <Select
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as BulkKind);
              setConfirm(false);
            }}
            className="h-9 w-56"
          >
            {(Object.keys(KIND_LABEL) as BulkKind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </Select>
          {kind === 'set_tier' ? (
            <Select
              value={tier}
              onChange={(e) => setTier(e.target.value as Tier)}
              className="h-9 w-36"
            >
              {(['pozyskaj', 'preview', 'start', 'pro', 'pro_bundle', 'multi'] as Tier[]).map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          ) : null}
          <Button type="button" disabled={busy} onClick={onClick} variant={confirm ? 'primary' : 'secondary'}>
            <PlayCircle className="size-4" aria-hidden="true" />
            {busy ? 'Wykonuję…' : confirm ? `Potwierdź — ${count} klinik` : 'Wykonaj'}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            <X className="size-4" aria-hidden="true" />
            Wyczyść
          </Button>
        </div>
        {confirm ? (
          <p className="text-xs text-warning">
            Operacja wpłynie na {count} klinik. Kliknij ponownie, aby potwierdzić.
          </p>
        ) : null}
        {error ? (
          <p className="rounded-md bg-error/10 px-3 py-2 text-xs text-error ring-1 ring-error/20">
            {error}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function BulkJobsPanel({ jobs, loading }: { jobs: BulkJob[]; loading: boolean }) {
  if (loading && jobs.length === 0) return null;
  if (jobs.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Ostatnie operacje masowe</CardTitle>
        <CardDescription>
          Lista 50 ostatnich. Regeneracje są kolejkowane — build-runner przejmuje je
          z ograniczeniem 1 równolegle (chroni vLLM GPU przed saturacją).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {jobs.slice(0, 20).map((j) => {
          const Icon = KIND_ICON[j.kind];
          const pct = j.total ? Math.round(((j.succeeded + j.failed) / j.total) * 100) : 0;
          return (
            <div
              key={j.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-surface p-3 ring-1 ring-black/5"
            >
              <div className="flex items-center gap-2 text-xs">
                <Icon className="size-3.5 text-text-muted" aria-hidden="true" />
                <span className="font-mono text-text">{KIND_LABEL[j.kind]}</span>
                <span className="text-text-subtle">
                  · {new Date(j.created_at * 1000).toLocaleString('pl-PL')}
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="tabular-nums">
                  {j.succeeded}/{j.total} ✓
                </span>
                {j.failed > 0 ? <span className="tabular-nums text-error">{j.failed} ✗</span> : null}
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 ring-1',
                    j.state === 'completed'
                      ? 'bg-success/10 text-success ring-success/20'
                      : j.state === 'failed'
                      ? 'bg-error/10 text-error ring-error/20'
                      : j.state === 'cancelled'
                      ? 'bg-surface text-text-subtle ring-border'
                      : 'bg-accent/10 text-accent ring-accent/20'
                  )}
                >
                  {j.state} ({pct}%)
                </span>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function useDebouncedValue<T>(value: T, ms: number): T {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}
